import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Users, Shield, HeartHandshake, ArrowRight, Heart } from 'lucide-react';

export const AboutPage = () => {
  const { t } = useLanguage();
  usePageMeta('about.title', 'seo.aboutDescription');

  const values = [
    {
      icon: Target,
      title: t('about.value1Title'),
      description: t('about.value1Desc'),
    },
    {
      icon: Users,
      title: t('about.value2Title'),
      description: t('about.value2Desc'),
    },
    {
      icon: Shield,
      title: t('about.value3Title'),
      description: t('about.value3Desc'),
    },
    {
      icon: HeartHandshake,
      title: t('about.value4Title'),
      description: t('about.value4Desc'),
    },
    {
      icon: Heart,
      title: t('about.value5Title'),
      description: t('about.value5Desc'),
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('about.title')}
            </h1>
            <p className="text-xl text-gray-600">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <p className="text-gray-700 leading-relaxed text-lg">
                {t('about.p1')}
              </p>
              <p className="text-gray-700 leading-relaxed text-lg">
                {t('about.p2')}
              </p>
              <p className="text-gray-700 leading-relaxed text-lg">
                {t('about.p3')}
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_settle-denmark/artifacts/1knrphn3_11.jpg"
                  alt="Next Step Denmark Founders"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <p className="text-sm text-gray-500 mt-3 text-center italic">
                {t('about.storyImageCaption')}
              </p>
            </div>
          </div>

          <div className="mt-12 space-y-6 max-w-4xl mx-auto">
            <p className="text-gray-900 leading-relaxed text-2xl font-semibold border-l-4 border-sea-blue pl-6">
              {t('about.p4')}
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              {t('about.p5')}
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              {t('about.p6')}
            </p>
          </div>
        </div>
      </section>

      {/* Founder Section - Alexandra */}
      <section className="py-20 bg-gradient-to-br from-sea-blue/5 via-white to-bronze/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              {t('about.founderTitle')}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 items-start">
              <div className="md:col-span-1">
                <div className="aspect-square rounded-lg overflow-hidden shadow-xl">
                  <img
                    src={t('about.founderImage')}
                    alt="Alexandra - Founder of Next Step Denmark"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-3 text-center italic">
                  {t('about.founderImageCaption')}
                </p>
              </div>
              
              <div className="md:col-span-2 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  {t('about.founderP1')}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {t('about.founderP2')}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {t('about.founderP3')}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {t('about.founderP4')}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  {t('about.founderP5')}
                </p>
                <p className="text-gray-900 font-semibold mt-4">
                  {t('about.founderLanguages')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How I Can Help You Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              {t('about.helpTitle')}
            </h2>
            
            <p className="text-gray-700 leading-relaxed text-lg mb-8 text-center">
              {t('about.helpIntro')}
            </p>
            
            <p className="text-gray-900 font-semibold text-lg mb-4">
              {t('about.helpSupport')}
            </p>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-sea-blue mr-3">•</span>
                <span className="text-gray-700">{t('about.helpPoint1')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sea-blue mr-3">•</span>
                <span className="text-gray-700">{t('about.helpPoint2')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sea-blue mr-3">•</span>
                <span className="text-gray-700">{t('about.helpPoint3')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sea-blue mr-3">•</span>
                <span className="text-gray-700">{t('about.helpPoint4')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-sea-blue mr-3">•</span>
                <span className="text-gray-700">{t('about.helpPoint5')}</span>
              </li>
            </ul>
            
            <p className="text-gray-700 leading-relaxed text-lg">
              {t('about.helpGoal')}
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-bronze/5 via-white to-sea-blue/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('about.valuesTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card
                  key={index}
                  className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-sea-blue/20"
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="w-14 h-14 rounded-full bg-sea-blue/10 flex items-center justify-center group-hover:bg-sea-blue group-hover:scale-110 transition-all duration-300">
                      <Icon className="h-7 w-7 text-sea-blue group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-sea-blue to-sea-blue/90 rounded-2xl p-12 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('about.ctaTitle')}
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              {t('about.ctaSubtitle')}
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-bronze hover:bg-bronze/90 text-white">
                {t('about.ctaButton')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
